'use client';
import { FC, ReactNode } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
} from '@radix-ui/react-dialog';
import { FocusScope } from '@radix-ui/react-focus-scope';
import classNames from 'classnames';

import { icon_close_large } from '@gn-audio/icons';

import { JabraIcon } from './JabraIcon/JabraIcon';

export interface GNDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  children?: ReactNode;
  fullScreen?: boolean;
  title?: string | ReactNode;
}

export const JabraDialog: FC<GNDialogProps> = ({
  title,
  open,
  fullScreen,
  children,
  onOpenChange,
  onClose,
}) => {
  const handleOpenChange = (open: boolean): void => {
    onOpenChange && onOpenChange(open);
    !open && onClose && onClose();
  };

  const sizeClass = fullScreen
    ? 'h-svh w-svw  '
    : 'w-full md:max-w-2xl max-md:h-svh max-md:w-svw md:max-h-[calc(100vh-2rem)]';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogContent
          className={classNames(
            `fixed left-[50%] top-[50%] z-50 grid translate-x-[-50%] translate-y-[-50%] gap-3 overflow-auto scroll-auto bg-white shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-1/3`,
            sizeClass
          )}
        >
          <FocusScope trapped={open}>
            <div className="flex h-full flex-col overflow-auto">
              <div className="mb-3 mt-16 text-center text-[1.5rem]">{title}</div>
              <div className="flex-1">{children}</div>

              <DialogClose className="text-muted-foreground absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-gray-50 font-bold opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none">
                <JabraIcon className="h-4" Icon={icon_close_large} />
              </DialogClose>
            </div>
          </FocusScope>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
